import React, { useCallback } from "react";
import { styled } from "styled-components";
import { ProgressAnimation, Icon, Spinner, HoverForHelp } from "components/common/index";
import { Button, DropdownButton, MenuItem, ButtonGroup } from "components/bootstrap";
import ReadableDuration from "components/common/ReadableDuration";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import { durationToMS } from "util/DateTime";
const FlexibleButtonGroup = styled(ButtonGroup)`
  display: flex;
  justify-content: flex-end;
  position: relative;

  > .btn-group {
    .btn:first-child {
      max-width: 100%;
    }
  }
`;
const ButtonLabel = ({ children }) => {
  const { refreshConfig } = useAutoRefresh();
  if (!(refreshConfig == null ? void 0 : refreshConfig.enabled)) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "Not updating", children);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, "Every ", /* @__PURE__ */ React.createElement(ReadableDuration, { duration: refreshConfig.interval }), children);
};
const RefreshControls = ({
  children = null,
  humanName,
  disable,
  onToggle = null,
  onEnable = null,
  onDisable = null,
  intervalOptions,
  onSelectInterval = null,
  isLoadingMinimumInterval,
  minimumRefreshInterval,
  defaultInterval
}) => {
  const { refreshConfig, startAutoRefresh, stopAutoRefresh, animationId } = useAutoRefresh();
  const selectInterval = useCallback(
    (interval) => {
      startAutoRefresh(durationToMS(interval));
      if (typeof onSelectInterval === "function") {
        onSelectInterval(interval);
      }
    },
    [onSelectInterval, startAutoRefresh]
  );
  const toggleEnable = useCallback(() => {
    var _a;
    if (!defaultInterval && !(refreshConfig == null ? void 0 : refreshConfig.interval)) {
      return;
    }
    if (typeof onToggle === "function") {
      onToggle(!(refreshConfig == null ? void 0 : refreshConfig.enabled));
    }
    if (refreshConfig == null ? void 0 : refreshConfig.enabled) {
      stopAutoRefresh();
      if (typeof onDisable === "function") {
        onDisable();
      }
    } else {
      startAutoRefresh((_a = refreshConfig == null ? void 0 : refreshConfig.interval) != null ? _a : durationToMS(defaultInterval));
      if (typeof onEnable === "function") {
        onEnable();
      }
    }
  }, [
    defaultInterval,
    onDisable,
    onEnable,
    onToggle,
    refreshConfig == null ? void 0 : refreshConfig.enabled,
    refreshConfig == null ? void 0 : refreshConfig.interval,
    startAutoRefresh,
    stopAutoRefresh
  ]);
  return /* @__PURE__ */ React.createElement(FlexibleButtonGroup, { "aria-label": `Refresh ${humanName} Controls` }, (refreshConfig == null ? void 0 : refreshConfig.enabled) && animationId && /* @__PURE__ */ React.createElement(
    ProgressAnimation,
    {
      key: `${refreshConfig.interval}-${animationId}`,
      $animationDuration: refreshConfig.interval,
      $increase: false
    }
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      onClick: toggleEnable,
      title: (refreshConfig == null ? void 0 : refreshConfig.enabled) ? "Pause Refresh" : "Start Refresh",
      disabled: disable || isLoadingMinimumInterval || !defaultInterval
    },
    /* @__PURE__ */ React.createElement(Icon, { name: (refreshConfig == null ? void 0 : refreshConfig.enabled) ? "pause" : "update" })
  ), /* @__PURE__ */ React.createElement(DropdownButton, { title: /* @__PURE__ */ React.createElement(ButtonLabel, null, children), disabled: disable, id: "refresh-options-dropdown" }, isLoadingMinimumInterval && /* @__PURE__ */ React.createElement(Spinner, null), !isLoadingMinimumInterval && intervalOptions.map(([interval, label]) => {
    const isBelowMinimum = durationToMS(interval) < durationToMS(minimumRefreshInterval);
    return /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        key: `RefreshControls-${label}`,
        onClick: () => selectInterval(interval),
        disabled: isBelowMinimum
      },
      label,
      isBelowMinimum && /* @__PURE__ */ React.createElement(HoverForHelp, { displayLeftMargin: true }, "Interval of ", /* @__PURE__ */ React.createElement(ReadableDuration, { duration: interval }), " (", interval, ") is below configured minimum interval of ", /* @__PURE__ */ React.createElement(ReadableDuration, { duration: minimumRefreshInterval }), " (", minimumRefreshInterval, ").")
    );
  })));
};
export default RefreshControls;
